"use client";
import { createContext, useContext } from "react";
import { useRouter } from "next/navigation";
import { saveBooking } from "lib/actionFetch";
import {
  useBookingStore,
  persistBookingData,
  clearPersistedBooking,
} from "./bookingStore";

const BookingContext = createContext({});

export const BookingProvider = ({ children }) => {
  const router = useRouter();
  const bookingData = useBookingStore((state) => state.bookingData);
  const loading = useBookingStore((state) => state.loading);
  const setLoading = useBookingStore((state) => state.setLoading);
  const setBookingData = useBookingStore((state) => state.setBookingData);
  const clearBookingData = useBookingStore((state) => state.clearBookingData);
  const hydrateBooking = useBookingStore((state) => state.hydrateBooking);

  const handleSideBooking = (values) => {
    setLoading(true);
    setBookingData(values);
    persistBookingData(values);
    setLoading(false);
    router.push("/checkout");
  };

  const getBooking = () => {
    if (!bookingData) {
      setLoading(true);
      hydrateBooking();
      setLoading(false);
    }
  };

  const createBooking = async (values, options = {}) => {
    try {
      setLoading(true);
      const data = { ...bookingData, ...values };

      data.tour = data.data?._id;
      data.departureDate = data.date?.toISOString?.() || data.date;
      data.preferredArrivalDate =
        data.preferredArrivalDate?.toISOString?.() || data.preferredArrivalDate;
      data.payAmount = parseInt(data.totalPrice);
      data.infant = data.infants ?? data.infant ?? 0;
      if (data.specialRequirements) {
        data.specialRequest = data.specialRequirements;
      }

      data.routeIndex = Number.isFinite(data.routeIndex) ? data.routeIndex : 0;
      data.routeTitle = data.route?.title || data.routeTitle || "";
      data.routeDays = data.route?.days || data.routeDays || 0;
      data.routeNights = data.route?.nights || data.routeNights || 0;

      data.pricePerAdult = data.pricePerAdult ?? data.basePrice ?? 0;
      data.pricePerChild = data.priceChildren ?? 0;
      data.pricePerInfant = data.priceInfant ?? 0;
      data.basePrice = data.basePrice ?? 0;
      data.salePrice = data.salePrice ?? 0;
      data.discountAmount = data.discountAmount ?? 0;
      data.pricingRule = data.pricingRule || null;

      delete data.data;
      delete data.date;
      delete data.route;
      delete data.infants;

      await saveBooking(data, options);

      clearBookingData();
      clearPersistedBooking();
      router.push("/");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BookingContext.Provider
      value={{
        handleSideBooking,
        bookingData,
        getBooking,
        createBooking,
        loading,
        setLoading,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBookingContext = () => useContext(BookingContext);
